import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import client from '../../services/graphqlClient';
import { useNotification } from '../../context/NotificationContext';
import './Marketing.css';

const MESES = ['', 'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const MESES_CORTO = ['', 'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

const formatCurrency = (val) => {
  if (!val || val === 0) return '-';
  return '$ ' + Math.round(val).toLocaleString('es-CO');
};

const formatPct = (val) => {
  if (!val || isNaN(val)) return '-';
  return (val * 100).toFixed(1) + '%';
};

const formatNumber = (val) => {
  if (!val || val === 0) return '-';
  return Math.round(val).toLocaleString('es-CO');
};

export default function Marketing() {
  const { success, error } = useNotification();
  const [anio, setAnio] = useState(new Date().getFullYear());
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);

  // Data states
  const [dashboard, setDashboard] = useState([]);
  const [costosFijos, setCostosFijos] = useState(0);
  const [ventas, setVentas] = useState([]);
  const [metas, setMetas] = useState([]);
  const [costosList, setCostosList] = useState([]);
  const [detalleMes, setDetalleMes] = useState(null);
  const [selectedMes, setSelectedMes] = useState(null);

  // Form states
  const [showVentaForm, setShowVentaForm] = useState(false);
  const [ventaForm, setVentaForm] = useState({ mes: new Date().getMonth() + 1, nombre_cliente: '', proyecto: '', valor: '', dias_gestion: '' });
  const [showIndicadorForm, setShowIndicadorForm] = useState(false);
  const [indicadorForm, setIndicadorForm] = useState({ mes: 1, ventas_totales: '', inversion_pauta: '', total_leads: '', total_agendamientos: '' });
  const [mesSeleccionadoVentas, setMesSeleccionadoVentas] = useState(new Date().getMonth() + 1);

  // Chart zoom & pan
  const [chartZoom, setChartZoom] = useState(1);
  const [chartPan, setChartPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [chartTooltip, setChartTooltip] = useState(null);
  const chartContainerRef = useRef(null);
  const [showMetaForm, setShowMetaForm] = useState(false);
  const [metaForm, setMetaForm] = useState({ mes: 1, ventas_meta: '', meta_dinero: '', agendamientos_meta: '', leads_meta: '', cpl_meta: '', inversion_meta: '' });
  const [showCostoForm, setShowCostoForm] = useState(false);
  const [costoForm, setCostoForm] = useState({ descripcion: '', valor: '' });
  const [editingVenta, setEditingVenta] = useState(null);
  const [editingCosto, setEditingCosto] = useState(null);
  const [guardando, setGuardando] = useState(false);

  // ============ FETCH FUNCTIONS ============
  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const data = await client.query(`query($anio: Int!) { marketingDashboard(anio: $anio) }`, { anio });
      if (data.marketingDashboard) {
        setDashboard(data.marketingDashboard.meses || []);
        setCostosFijos(data.marketingDashboard.costos_fijos || []);
      }
    } catch (err) {
      error('Error cargando dashboard');
    } finally {
      setLoading(false);
    }
  }, [anio, error]);

  const fetchVentas = useCallback(async () => {
    try {
      const data = await client.query(`query($anio: Int!) { marketingVentasLista(anio: $anio) { id anio mes nombre_cliente proyecto valor dias_gestion created_at } }`, { anio });
      setVentas(data.marketingVentasLista || []);
    } catch (err) {
      error('Error cargando ventas');
    }
  }, [anio, error]);

  const fetchMetas = useCallback(async () => {
    try {
      const data = await client.query(`query($anio: Int!) { marketingMetasLista(anio: $anio) { id anio mes ventas_meta agendamientos_meta leads_meta cpl_meta inversion_meta meta_dinero } }`, { anio });
      setMetas(data.marketingMetasLista || []);
    } catch (err) {
      error('Error cargando metas');
    }
  }, [anio, error]);

  const fetchCostos = useCallback(async () => {
    try {
      const data = await client.query(`query($anio: Int!) { marketingCostosLista(anio: $anio) { id anio descripcion valor } }`, { anio });
      setCostosList(data.marketingCostosLista || []);
    } catch (err) {
      error('Error cargando costos');
    }
  }, [anio, error]);

  const fetchDetalleMes = useCallback(async (mes) => {
    try {
      const data = await client.query(`query($anio: Int!, $mes: Int!) { marketingDetalleMes(anio: $anio, mes: $mes) }`, { anio, mes });
      if (data.marketingDetalleMes) setDetalleMes(data.marketingDetalleMes);
    } catch (err) {
      error('Error cargando detalle');
    }
  }, [anio, error]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  useEffect(() => {
    if (activeTab === 'ventas') { fetchVentas(); fetchDashboard(); }
    if (activeTab === 'metas') { fetchMetas(); fetchCostos(); }
  }, [activeTab, fetchVentas, fetchMetas, fetchCostos, fetchDashboard]);

  // ============ HANDLERS ============
  const handleGuardarVenta = async (e) => {
    e.preventDefault();
    if (!ventaForm.nombre_cliente || !ventaForm.proyecto || !ventaForm.valor) {
      error('Completa todos los campos'); return;
    }
    setGuardando(true);
    try {
      const gqlR = await client.mutate(`mutation($anio: Int!, $input: VentaMarketingInput!) { crearVentaMarketing(anio: $anio, input: $input) { success message } }`, { anio, input: { mes: parseInt(ventaForm.mes), nombre_cliente: ventaForm.nombre_cliente, proyecto: ventaForm.proyecto, valor: parseFloat(ventaForm.valor), dias_gestion: parseInt(ventaForm.dias_gestion) || 0 } });
      if (gqlR.crearVentaMarketing.success) {
        success('Venta registrada');
        setShowVentaForm(false);
        setVentaForm({ mes: ventaForm.mes, nombre_cliente: '', proyecto: '', valor: '', dias_gestion: '' });
        fetchVentas();
        fetchDashboard();
      } else error('Error al guardar');
    } catch (err) { error('Error guardando venta'); }
    finally { setGuardando(false); }
  };

  const handleEliminarVenta = async (id) => {
    if (!window.confirm('¿Eliminar esta venta?')) return;
    try {
      const gqlR = await client.mutate(`mutation($id: Int!) { eliminarVentaMarketing(id: $id) { success message } }`, { id });
      if (gqlR.eliminarVentaMarketing.success) { success('Venta eliminada'); fetchVentas(); fetchDashboard(); }
      else error(gqlR.eliminarVentaMarketing.message);
    } catch (err) { error('Error eliminando venta'); }
  };

  const handleEditarVenta = async (e) => {
    e.preventDefault();
    if (!editingVenta) return;
    setGuardando(true);
    try {
      const gqlR = await client.mutate(`mutation($id: Int!, $input: VentaMarketingInput!) { editarVentaMarketing(id: $id, input: $input) { success message } }`, { id: editingVenta.id, input: { mes: parseInt(editingVenta.mes), nombre_cliente: editingVenta.nombre_cliente, proyecto: editingVenta.proyecto, valor: parseFloat(editingVenta.valor), dias_gestion: parseInt(editingVenta.dias_gestion) || 0 } });
      if (gqlR.editarVentaMarketing.success) {
        success('Venta actualizada');
        setEditingVenta(null);
        fetchVentas();
        fetchDashboard();
      } else error('Error en operación');
    } catch (err) { error('Error actualizando venta'); }
    finally { setGuardando(false); }
  };

  const handleGuardarIndicadores = async (e) => {
    e.preventDefault();
    setGuardando(true);
    try {
      const gqlR = await client.mutate(`mutation($anio: Int!, $mes: Int!, $input: IndicadorMarketingInput!) { guardarIndicadorMarketing(anio: $anio, mes: $mes, input: $input) { success message } }`, { anio, mes: parseInt(indicadorForm.mes), input: { ventas_totales: parseInt(indicadorForm.ventas_totales) || 0, inversion_pauta: parseFloat(indicadorForm.inversion_pauta) || 0, total_leads: parseInt(indicadorForm.total_leads) || 0, total_agendamientos: parseInt(indicadorForm.total_agendamientos) || 0 } });
      if (gqlR.guardarIndicadorMarketing.success) {
        success('Indicadores guardados');
        setShowIndicadorForm(false);
        fetchDashboard();
        fetchVentas();
      } else error('Error en operación');
    } catch (err) { error('Error guardando indicadores'); }
    finally { setGuardando(false); }
  };

  const handleGuardarMeta = async (e) => {
    e.preventDefault();
    setGuardando(true);
    try {
      const gqlR = await client.mutate(`mutation($anio: Int!, $input: MetaMarketingInput!) { guardarMetaMarketing(anio: $anio, input: $input) { success message } }`, { anio, input: { mes: parseInt(metaForm.mes), ventas_meta: parseInt(metaForm.ventas_meta) || 0, agendamientos_meta: parseInt(metaForm.agendamientos_meta) || 0, leads_meta: parseInt(metaForm.leads_meta) || 0, cpl_meta: parseFloat(metaForm.cpl_meta) || 0, inversion_meta: parseFloat(metaForm.inversion_meta) || 0, meta_dinero: parseFloat(metaForm.meta_dinero) || 0 } });
      if (gqlR.guardarMetaMarketing.success) { success('Meta guardada'); setShowMetaForm(false); fetchMetas(); fetchDashboard(); }
      else error('Error en la operación');
    } catch (err) { error('Error guardando meta'); }
    finally { setGuardando(false); }
  };

  const handleGuardarCosto = async (e) => {
    e.preventDefault();
    setGuardando(true);
    try {
      const gqlR = await client.mutate(`mutation($anio: Int!, $descripcion: String!, $valor: Float!) { crearCostoFijoMarketing(anio: $anio, descripcion: $descripcion, valor: $valor) { success message } }`, { anio, descripcion: costoForm.descripcion, valor: parseFloat(costoForm.valor) });
      if (gqlR.crearCostoFijoMarketing.success) { success('Costo agregado'); setShowCostoForm(false); setCostoForm({ descripcion: '', valor: '' }); fetchCostos(); fetchDashboard(); }
      else error('Error en la operación');
    } catch (err) { error('Error guardando costo'); }
    finally { setGuardando(false); }
  };

  const handleEliminarCosto = async (id) => {
    if (!window.confirm('¿Eliminar este costo fijo?')) return;
    try {
      const gqlR = await client.mutate(`mutation($id: Int!) { eliminarCostoFijoMarketing(id: $id) { success message } }`, { id });
      if (gqlR.eliminarCostoFijoMarketing.success) { success('Costo eliminado'); fetchCostos(); fetchDashboard(); }
    } catch (err) { error('Error eliminando costo'); }
  };

  const handleEditarCosto = async (e) => {
    e.preventDefault();
    if (!editingCosto) return;
    setGuardando(true);
    try {
      const gqlR = await client.mutate(`mutation($id: Int!, $descripcion: String!, $valor: Float!) { editarCostoFijoMarketing(id: $id, descripcion: $descripcion, valor: $valor) { success message } }`, { id: editingCosto.id, descripcion: editingCosto.descripcion, valor: parseFloat(editingCosto.valor) });
      if (gqlR.editarCostoFijoMarketing.success) {
        success('Costo actualizado');
        setEditingCosto(null);
        fetchCostos();
        fetchDashboard();
      } else error('Error en operación');
    } catch (err) { error('Error actualizando costo'); }
    finally { setGuardando(false); }
  };

  // Guardar un campo individual de indicadores (sin borrar otros)
  const handleGuardarIndicadorCampo = async (mes, campo, valor) => {
    try {
      const inputData = {};
      inputData[campo] = typeof valor === 'string' ? (campo.includes('inversion') ? parseFloat(valor) || 0 : parseInt(valor) || 0) : valor;
      const gqlR = await client.mutate(`mutation($anio: Int!, $mes: Int!, $input: IndicadorMarketingInput!) { guardarIndicadorMarketing(anio: $anio, mes: $mes, input: $input) { success message } }`, { anio, mes, input: inputData });
      if (gqlR.guardarIndicadorMarketing.success) {
        success(`${campo.replace(/_/g, ' ')} actualizado`);
        fetchDashboard();
      } else error('Error en operación');
    } catch (err) { error('Error guardando indicador'); }
  };

  const openDetalleMes = (mes) => {
    setSelectedMes(mes);
    fetchDetalleMes(mes);
    setActiveTab('detalle');
  };

  // openEditIndicadores ya no se usa (dashboard es solo lectura)

  const openEditMeta = (meta) => {
    setMetaForm({
      mes: meta.mes || meta,
      ventas_meta: meta.ventas_meta || '',
      meta_dinero: meta.meta_dinero || '',
      agendamientos_meta: meta.agendamientos_meta || '',
      leads_meta: meta.leads_meta || '',
      cpl_meta: meta.cpl_meta || '',
      inversion_meta: meta.inversion_meta || ''
    });
    setShowMetaForm(true);
  };

  // ============ RESUMEN ACUMULADO ============
  const resumen = useMemo(() => {
    const mesesConDatos = dashboard.filter(d => d.tiene_datos);
    if (mesesConDatos.length === 0) return null;
    return {
      totalVentasMkt: mesesConDatos.reduce((s, d) => s + d.ventas_marketing, 0),
      totalVentasDinero: mesesConDatos.reduce((s, d) => s + d.ventas_marketing_dinero, 0),
      totalLeads: mesesConDatos.reduce((s, d) => s + d.total_leads, 0),
      totalAgend: mesesConDatos.reduce((s, d) => s + d.total_agendamientos, 0),
      totalInversion: mesesConDatos.reduce((s, d) => s + d.inversion_pauta, 0),
      totalCostos: mesesConDatos.reduce((s, d) => s + d.costos_marketing, 0),
      promedioRoas: mesesConDatos.reduce((s, d) => s + d.roas, 0) / mesesConDatos.length,
      promedioCac: mesesConDatos.filter(d => d.cac_total > 0).reduce((s, d) => s + d.cac_total, 0) / (mesesConDatos.filter(d => d.cac_total > 0).length || 1),
    };
  }, [dashboard]);

  // Ventas agrupadas por mes
  const ventasPorMes = useMemo(() => {
    const map = {};
    ventas.forEach(v => {
      const m = parseInt(v.mes);
      if (!map[m]) map[m] = [];
      map[m].push(v);
    });
    return map;
  }, [ventas]);

  // ============ RENDER ============
  if (loading && activeTab === 'dashboard') {
    return <div className="mkt-loading"><div className="spinner"></div><p>Cargando Marketing...</p></div>;
  }

  return (
    <div className="marketing-module">
      {/* Header */}
      <div className="mkt-header">
        <div className="mkt-header-left">
          <h1>Marketing</h1>
          <p>Indicadores y seguimiento de marketing digital</p>
        </div>
        <div className="mkt-header-right">
          <span className="mkt-anio-badge">{anio}</span>
        </div>
      </div>

      {/* Resumen cards */}
      {resumen && activeTab === 'dashboard' && (
        <div className="mkt-resumen-cards">
          <div className="mkt-card accent-blue">
            <span className="mkt-card-label">Ventas Marketing {anio}</span>
            <span className="mkt-card-value">{resumen.totalVentasMkt}</span>
            <span className="mkt-card-sub">{formatCurrency(resumen.totalVentasDinero)}</span>
          </div>
          <div className="mkt-card accent-green">
            <span className="mkt-card-label">Ventas Acumuladas</span>
            <span className="mkt-card-value">{formatCurrency(resumen.totalVentasDinero)}</span>
            <span className="mkt-card-sub">{resumen.totalVentasMkt} ventas cerradas</span>
          </div>
          <div className="mkt-card accent-purple">
            <span className="mkt-card-label">ROAS Promedio</span>
            <span className="mkt-card-value">{resumen.promedioRoas.toFixed(1)}x</span>
            <span className="mkt-card-sub">Retorno sobre inversión</span>
          </div>
          <div className="mkt-card accent-orange">
            <span className="mkt-card-label">CAC Promedio</span>
            <span className="mkt-card-value">{formatCurrency(resumen.promedioCac)}</span>
            <span className="mkt-card-sub">Inversión: {formatCurrency(resumen.totalInversion)}</span>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="mkt-tabs">
        <button className={`mkt-tab ${activeTab === 'dashboard' ? 'active' : ''}`} onClick={() => setActiveTab('dashboard')}>
          Dashboard
        </button>
        <button className={`mkt-tab ${activeTab === 'ventas' ? 'active' : ''}`} onClick={() => setActiveTab('ventas')}>
          Registro Ventas
        </button>
        <button className={`mkt-tab ${activeTab === 'metas' ? 'active' : ''}`} onClick={() => setActiveTab('metas')}>
          Metas {anio}
        </button>
        {activeTab === 'detalle' && (
          <button className="mkt-tab active">
            {MESES[selectedMes]} {anio}
          </button>
        )}
      </div>

      {/* TAB: Dashboard */}
      {activeTab === 'dashboard' && (
        <div className="mkt-dashboard">
          {/* Gráfica de líneas */}
          {resumen && (
            <div className="mkt-chart-container">
              <div className="mkt-chart-header">
                <h2>Tendencia de Ventas {anio}</h2>
              </div>
              <div className="mkt-chart"
                ref={chartContainerRef}
                style={{ overflow: 'hidden', position: 'relative' }}
              >
                {(() => {
                  const mesesConDatos = dashboard.filter(d => d.tiene_datos);
                  if (mesesConDatos.length < 1) return <p className="mkt-empty">Sin datos para graficar</p>;

                  // Escalas independientes con margen de 20% arriba
                  const rawMaxU = Math.max(...mesesConDatos.map(d => d.ventas_marketing), 1);
                  const rawMaxD = Math.max(...mesesConDatos.map(d => d.ventas_marketing_dinero), 1);
                  const maxVentas = Math.ceil(rawMaxU * 1.2);
                  const maxDinero = Math.ceil(rawMaxD * 1.2);

                  const paddingTop = 25;
                  const paddingBottom = 15;
                  const leftAxis = 45;
                  const rightAxis = 85;
                  const chartH = 300;
                  const chartW = 850;
                  const usableH = chartH - paddingTop - paddingBottom;
                  const plotLeft = leftAxis;
                  const plotRight = chartW - rightAxis;

                  const getX = (i) => plotLeft + (i / 11) * (plotRight - plotLeft);
                  const getYU = (val) => paddingTop + usableH - (val / maxVentas) * usableH;
                  const getYD = (val) => paddingTop + usableH - (val / maxDinero) * usableH;

                  const puntosUnidades = mesesConDatos.map(d => `${getX(d.mes - 1)},${getYU(d.ventas_marketing)}`).join(' ');
                  const puntosDinero = mesesConDatos.map(d => `${getX(d.mes - 1)},${getYD(d.ventas_marketing_dinero)}`).join(' ');
                  const areaDinero = `${getX(mesesConDatos[0].mes - 1)},${chartH - paddingBottom} ` + puntosDinero + ` ${getX(mesesConDatos[mesesConDatos.length-1].mes - 1)},${chartH - paddingBottom}`;
                  const areaUnidades = `${getX(mesesConDatos[0].mes - 1)},${chartH - paddingBottom} ` + puntosUnidades + ` ${getX(mesesConDatos[mesesConDatos.length-1].mes - 1)},${chartH - paddingBottom}`;

                  // Ticks para eje Y
                  const ticksU = [0, 0.25, 0.5, 0.75, 1];
                  const ticksD = [0, 0.25, 0.5, 0.75, 1];
                  const fmtShort = (v) => v >= 1000000000 ? (v/1000000000).toFixed(0) + 'B' : v >= 1000000 ? (v/1000000).toFixed(0) + 'M' : v >= 1000 ? (v/1000).toFixed(0) + 'K' : v.toString();

                  return (
                    <div className="mkt-chart-inner">
                      <svg viewBox={`0 0 ${chartW} ${chartH + 28}`} className="mkt-chart-svg" preserveAspectRatio="xMidYMid meet"
                        >

                        {/* Grid horizontal */}
                        {ticksU.map(p => {
                          const y = getYU(maxVentas * p);
                          return <line key={p} x1={plotLeft} x2={plotRight} y1={y} y2={y} stroke="#e5e7eb" strokeWidth="0.5" strokeDasharray={p === 0 ? '' : '4,4'} />;
                        })}

                        {/* Eje Y Izquierdo - Unidades (azul) */}
                        <line x1={plotLeft} x2={plotLeft} y1={paddingTop} y2={chartH - paddingBottom} stroke="#3b82f6" strokeWidth="1" opacity="0.3" />
                        {ticksU.map(p => {
                          const y = getYU(maxVentas * p);
                          const val = Math.round(maxVentas * p);
                          return (
                            <g key={`lu-${p}`}>
                              <line x1={plotLeft - 4} x2={plotLeft} y1={y} y2={y} stroke="#3b82f6" strokeWidth="1" />
                              <text x={plotLeft - 8} y={y + 4} textAnchor="end" fontSize="10" fill="#3b82f6" fontWeight="600">{val}</text>
                            </g>
                          );
                        })}
                        <text x={8} y={paddingTop - 8} fontSize="9" fill="#3b82f6" fontWeight="700">UNIDADES</text>

                        {/* Eje Y Derecho - Dinero (verde) */}
                        <line x1={plotRight} x2={plotRight} y1={paddingTop} y2={chartH - paddingBottom} stroke="#10b981" strokeWidth="1" opacity="0.3" />
                        {ticksD.map(p => {
                          const y = getYD(maxDinero * p);
                          const val = maxDinero * p;
                          return (
                            <g key={`ld-${p}`}>
                              <line x1={plotRight} x2={plotRight + 4} y1={y} y2={y} stroke="#10b981" strokeWidth="1" />
                              <text x={plotRight + 8} y={y + 4} textAnchor="start" fontSize="10" fill="#10b981" fontWeight="600">$ {fmtShort(val)}</text>
                            </g>
                          );
                        })}
                        <text x={plotRight + 8} y={paddingTop - 8} fontSize="9" fill="#10b981" fontWeight="700">VENTAS $</text>

                        {/* Areas */}
                        <polygon points={areaDinero} fill="#10b981" fillOpacity="0.06" />
                        <polygon points={areaUnidades} fill="#3b82f6" fillOpacity="0.06" />

                        {/* Líneas */}
                        <polyline points={puntosDinero} fill="none" stroke="#10b981" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
                        <polyline points={puntosUnidades} fill="none" stroke="#3b82f6" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />

                        {/* Eje X - Meses */}
                        {dashboard.map((d, i) => (
                          <text key={i} x={getX(i)} y={chartH + 14} textAnchor="middle" fontSize="11" fill="#6b7280" fontWeight="500">{MESES_CORTO[i + 1]}</text>
                        ))}

                        {/* Puntos interactivos con tooltip */}
                        {mesesConDatos.map(d => {
                          const x = getX(d.mes - 1);
                          const yU = getYU(d.ventas_marketing);
                          const yD = getYD(d.ventas_marketing_dinero);
                          const isActive = chartTooltip?.mes === d.mes;
                          return (
                            <g key={d.mes}
                              onMouseEnter={(e) => {
                                const rect = e.currentTarget.closest('svg').getBoundingClientRect();
                                setChartTooltip({ mes: d.mes, x: e.clientX - rect.left, y: Math.min(yU, yD), data: d });
                              }}
                              onMouseLeave={() => setChartTooltip(null)}
                              style={{ cursor: 'pointer' }}
                            >
                              {/* Línea vertical guía al hover */}
                              {isActive && <line x1={x} x2={x} y1={paddingTop} y2={chartH - paddingBottom} stroke="#94a3b8" strokeWidth="0.8" strokeDasharray="3,3" />}
                              {/* Área de hit invisible más grande */}
                              <circle cx={x} cy={yU} r="14" fill="transparent" />
                              <circle cx={x} cy={yD} r="14" fill="transparent" />
                              {/* Punto unidades */}
                              <circle cx={x} cy={yU} r={isActive ? 7 : 5} fill={isActive ? '#3b82f6' : '#fff'} stroke="#3b82f6" strokeWidth="2.5" style={{ transition: 'r 0.15s' }} />
                              {/* Punto dinero */}
                              <circle cx={x} cy={yD} r={isActive ? 7 : 5} fill={isActive ? '#10b981' : '#fff'} stroke="#10b981" strokeWidth="2.5" style={{ transition: 'r 0.15s' }} />
                            </g>
                          );
                        })}
                      </svg>
                      {/* Tooltip */}
                      {chartTooltip && (
                        <div className="mkt-chart-tooltip" style={{ left: chartTooltip.x + 'px', top: '10px' }}>
                          <div className="mkt-tooltip-header">{MESES[chartTooltip.data.mes]} {anio}</div>
                          <div className="mkt-tooltip-row blue">
                            <span className="mkt-tooltip-dot" style={{background:'#3b82f6'}}></span>
                            <span>Ventas Mkt:</span>
                            <strong>{chartTooltip.data.ventas_marketing} unidades</strong>
                          </div>
                          <div className="mkt-tooltip-row green">
                            <span className="mkt-tooltip-dot" style={{background:'#10b981'}}></span>
                            <span>Ventas $:</span>
                            <strong>{formatCurrency(chartTooltip.data.ventas_marketing_dinero)}</strong>
                          </div>
                          {chartTooltip.data.ventas_totales > 0 && (
                            <div className="mkt-tooltip-row gray">
                              <span>Ventas Totales:</span>
                              <strong>{chartTooltip.data.ventas_totales}</strong>
                            </div>
                          )}
                          {chartTooltip.data.roas > 0 && (
                            <div className="mkt-tooltip-row gray">
                              <span>ROAS:</span>
                              <strong>{chartTooltip.data.roas}x</strong>
                            </div>
                          )}
                        </div>
                      )}
                      <div className="mkt-chart-legend">
                        <span className="mkt-legend-item"><span className="mkt-legend-dot" style={{background:'#3b82f6'}}></span> Ventas (unidades) — Eje izquierdo</span>
                        <span className="mkt-legend-item"><span className="mkt-legend-dot" style={{background:'#10b981'}}></span> Ventas ($) — Eje derecho</span>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          )}

          <h2>Indicadores Mensuales</h2>
          <div className="mkt-table-wrapper">
            <table className="mkt-table">
              <thead>
                <tr>
                  <th className="sticky-col">Mes</th>
                  <th>Ventas Tot.</th>
                  <th>Ventas Mkt</th>
                  <th>Meta</th>
                  <th>Cumpl.</th>
                  <th>Ventas $</th>
                  <th>Meta $</th>
                  <th>Cumpl. $</th>
                  <th>Costos Mkt</th>
                  <th>Inversión</th>
                  <th>Leads</th>
                  <th>Meta L.</th>
                  <th>Cumpl. L.</th>
                  <th>Agend.</th>
                  <th>CPL</th>
                  <th>Tasa Conv.</th>
                  <th>% Vtas Mkt</th>
                  <th>ROAS</th>
                  <th>MQL/SQL</th>
                  <th>CAC Total</th>
                  <th>CAC Inv.</th>
                  <th>CAC %</th>
                  <th>AOV</th>
                  <th>Prom. Días</th>
                </tr>
              </thead>
              <tbody>
                {dashboard.map(d => (
                  <tr key={d.mes} className={d.tiene_datos ? '' : 'row-empty'}>
                    <td className="sticky-col mes-cell" onClick={() => d.tiene_datos && openDetalleMes(d.mes)}>
                      <strong>{MESES_CORTO[d.mes]}</strong>
                    </td>
                    <td>{d.tiene_datos ? d.ventas_totales : '-'}</td>
                    <td className="highlight-blue">{d.tiene_datos ? d.ventas_marketing : '-'}</td>
                    <td>{d.ventas_meta || '-'}</td>
                    <td className={d.cumplimiento_ventas >= 1 ? 'highlight-green' : d.cumplimiento_ventas > 0 ? 'highlight-orange' : ''}>
                      {d.tiene_datos ? formatPct(d.cumplimiento_ventas) : '-'}
                    </td>
                    <td>{d.tiene_datos ? formatCurrency(d.ventas_marketing_dinero) : '-'}</td>
                    <td>{d.meta_dinero ? formatCurrency(d.meta_dinero) : '-'}</td>
                    <td className={d.cumplimiento_dinero >= 1 ? 'highlight-green' : d.cumplimiento_dinero > 0 ? 'highlight-orange' : ''}>
                      {d.tiene_datos ? formatPct(d.cumplimiento_dinero) : '-'}
                    </td>
                    <td>{d.tiene_datos ? formatCurrency(d.costos_marketing) : '-'}</td>
                    <td>{d.tiene_datos ? formatCurrency(d.inversion_pauta) : '-'}</td>
                    <td>{d.tiene_datos ? formatNumber(d.total_leads) : '-'}</td>
                    <td>{d.leads_meta || '-'}</td>
                    <td className={d.cumplimiento_leads >= 1 ? 'highlight-green' : d.cumplimiento_leads > 0 ? 'highlight-orange' : ''}>
                      {d.tiene_datos ? formatPct(d.cumplimiento_leads) : '-'}
                    </td>
                    <td>{d.tiene_datos ? d.total_agendamientos : '-'}</td>
                    <td>{d.tiene_datos ? formatCurrency(d.cpl) : '-'}</td>
                    <td>{d.tiene_datos ? formatPct(d.tasa_conversion) : '-'}</td>
                    <td className="highlight-blue">{d.tiene_datos ? formatPct(d.pct_ventas_mkt) : '-'}</td>
                    <td className="highlight-green bold">{d.tiene_datos ? (Number(d.roas) || 0).toFixed(2) + 'x' : '-'}</td>
                    <td>{d.tiene_datos ? formatPct(d.mql_vs_sql) : '-'}</td>
                    <td>{d.tiene_datos ? formatCurrency(d.cac_total) : '-'}</td>
                    <td>{d.tiene_datos ? formatCurrency(d.cac_inversion) : '-'}</td>
                    <td>{d.tiene_datos ? formatPct(d.cac_pct) : '-'}</td>
                    <td>{d.tiene_datos ? formatCurrency(d.aov) : '-'}</td>
                    <td>{d.tiene_datos && d.prom_dias_gestion ? d.prom_dias_gestion + 'd' : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TAB: Registro Ventas */}
      {activeTab === 'ventas' && (
        <div className="mkt-ventas">
          {/* Selector de mes */}
          <div className="mkt-mes-selector">
            <h2>Registro de Ventas y Datos — {MESES[mesSeleccionadoVentas]} {anio}</h2>
            <div className="mkt-mes-tabs">
              {MESES.slice(1).map((m, i) => (
                <button key={i+1} className={`mkt-mes-tab ${mesSeleccionadoVentas === i+1 ? 'active' : ''}`} onClick={() => setMesSeleccionadoVentas(i+1)}>
                  {MESES_CORTO[i+1]}
                </button>
              ))}
            </div>
          </div>

          {/* Indicadores del mes */}
          {(() => {
            const datosMes = dashboard.find(d => d.mes === mesSeleccionadoVentas) || {};
            const ventasMktCount = (ventasPorMes[mesSeleccionadoVentas] || []).length;
            return (
              <div className="mkt-indicadores-mes">
                <h3>Indicadores del Mes</h3>
                <div className="mkt-indicadores-grid">
                  <div className="mkt-indicador-field">
                    <label>Ventas Marketing (auto)</label>
                    <input type="number" value={ventasMktCount} disabled className="mkt-input-disabled" />
                    <span className="mkt-field-hint">Se calcula del registro de ventas</span>
                  </div>
                  <div className="mkt-indicador-field">
                    <label>Ventas Totales Empresa</label>
                    <input type="number" defaultValue={datosMes.ventas_totales || ''} key={`vt-${mesSeleccionadoVentas}`}
                      onBlur={(e) => {
                        if (e.target.value !== '' && parseInt(e.target.value) !== (datosMes.ventas_totales || 0)) {
                          handleGuardarIndicadorCampo(mesSeleccionadoVentas, 'ventas_totales', e.target.value);
                        }
                      }} placeholder="0" />
                  </div>
                  <div className="mkt-indicador-field">
                    <label>Inversión en Pauta ($)</label>
                    <input type="number" defaultValue={datosMes.inversion_pauta || ''} key={`ip-${mesSeleccionadoVentas}`}
                      onBlur={(e) => {
                        if (e.target.value !== '' && parseFloat(e.target.value) !== (datosMes.inversion_pauta || 0)) {
                          handleGuardarIndicadorCampo(mesSeleccionadoVentas, 'inversion_pauta', e.target.value);
                        }
                      }} placeholder="0" />
                  </div>
                  <div className="mkt-indicador-field">
                    <label>Total Leads</label>
                    <input type="number" defaultValue={datosMes.total_leads || ''} key={`tl-${mesSeleccionadoVentas}`}
                      onBlur={(e) => {
                        if (e.target.value !== '' && parseInt(e.target.value) !== (datosMes.total_leads || 0)) {
                          handleGuardarIndicadorCampo(mesSeleccionadoVentas, 'total_leads', e.target.value);
                        }
                      }} placeholder="0" />
                  </div>
                  <div className="mkt-indicador-field">
                    <label>Total Agendamientos</label>
                    <input type="number" defaultValue={datosMes.total_agendamientos || ''} key={`ta-${mesSeleccionadoVentas}`}
                      onBlur={(e) => {
                        if (e.target.value !== '' && parseInt(e.target.value) !== (datosMes.total_agendamientos || 0)) {
                          handleGuardarIndicadorCampo(mesSeleccionadoVentas, 'total_agendamientos', e.target.value);
                        }
                      }} placeholder="0" />
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Ventas del mes seleccionado */}
          <div className="mkt-ventas-mes">
            <div className="mkt-ventas-mes-header">
              <h3>Ventas Marketing — {MESES[mesSeleccionadoVentas]}</h3>
              <div style={{display:'flex',gap:'8px',alignItems:'center'}}>
                <span className="mkt-ventas-mes-total">
                  {(ventasPorMes[mesSeleccionadoVentas] || []).length} ventas — {formatCurrency((ventasPorMes[mesSeleccionadoVentas] || []).reduce((s, v) => s + parseFloat(v.valor), 0))}
                </span>
                <button className="mkt-btn primary" onClick={() => { setVentaForm({ mes: mesSeleccionadoVentas, nombre_cliente: '', proyecto: '', valor: '', dias_gestion: '' }); setShowVentaForm(true); }}>+ Nueva Venta</button>
              </div>
            </div>
            {(ventasPorMes[mesSeleccionadoVentas] || []).length > 0 ? (
              <table className="mkt-table compact">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Cliente</th>
                    <th>Proyecto</th>
                    <th>Valor</th>
                    <th>Días Gest.</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {(ventasPorMes[mesSeleccionadoVentas] || []).map(v => (
                    <tr key={v.id}>
                      <td>{v.numero_venta}</td>
                      <td><strong>{v.nombre_cliente}</strong></td>
                      <td>{v.proyecto}</td>
                      <td className="highlight-blue">{formatCurrency(parseFloat(v.valor))}</td>
                      <td>{v.dias_gestion || '-'}</td>
                      <td style={{display:'flex',gap:'4px'}}>
                        <button className="mkt-btn-icon" onClick={() => setEditingVenta({ id: v.id, mes: v.mes || mesSeleccionadoVentas, nombre_cliente: v.nombre_cliente, proyecto: v.proyecto, valor: v.valor, dias_gestion: v.dias_gestion || 0 })} title="Editar">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                          </svg>
                        </button>
                        <button className="mkt-btn-icon danger" onClick={() => handleEliminarVenta(v.id)} title="Eliminar">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
                            <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="mkt-empty">No hay ventas registradas en {MESES[mesSeleccionadoVentas]}</p>
            )}
          </div>
        </div>
      )}

      {/* TAB: Metas */}
      {activeTab === 'metas' && (
        <div className="mkt-metas">
          {/* Costos Fijos */}
          <div className="mkt-section">
            <div className="mkt-table-toolbar">
              <h2>Costos Fijos Marketing {anio}</h2>
              <button className="mkt-btn primary" onClick={() => setShowCostoForm(true)}>+ Agregar Costo</button>
            </div>
            <div className="mkt-costos-grid">
              {costosList.map(c => (
                <div key={c.id} className="mkt-costo-card">
                  <div className="mkt-costo-info">
                    <span className="mkt-costo-desc">{c.descripcion}</span>
                    <span className="mkt-costo-val">{formatCurrency(parseFloat(c.valor))}</span>
                  </div>
                  <div style={{display:'flex',gap:'4px'}}>
                    <button className="mkt-btn-icon" onClick={() => setEditingCosto({ id: c.id, descripcion: c.descripcion, valor: c.valor })} title="Editar">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                      </svg>
                    </button>
                    <button className="mkt-btn-icon danger" onClick={() => handleEliminarCosto(c.id)} title="Eliminar">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
                        <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
              <div className="mkt-costo-card total">
                <span>Total Costos Fijos</span>
                <span className="mkt-costo-val">{formatCurrency(costosList.reduce((s, c) => s + parseFloat(c.valor), 0))}</span>
              </div>
            </div>
          </div>

          {/* Metas por Mes */}
          <div className="mkt-section">
            <div className="mkt-table-toolbar">
              <h2>Metas Mensuales {anio}</h2>
              <button className="mkt-btn primary" onClick={() => { setMetaForm({ mes: 1, ventas_meta: '', meta_dinero: '', agendamientos_meta: '', leads_meta: '', cpl_meta: '', inversion_meta: '' }); setShowMetaForm(true); }}>+ Editar Meta</button>
            </div>
            <div className="mkt-table-wrapper">
              <table className="mkt-table">
                <thead>
                  <tr>
                    <th>Mes</th>
                    <th>Ventas Meta</th>
                    <th>Meta $</th>
                    <th>Agendamientos</th>
                    <th>Leads</th>
                    <th>CPL</th>
                    <th>Inversión</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {metas.map(m => (
                    <tr key={m.mes}>
                      <td><strong>{MESES[parseInt(m.mes)]}</strong></td>
                      <td>{m.ventas_meta}</td>
                      <td>{formatCurrency(parseFloat(m.meta_dinero))}</td>
                      <td>{m.agendamientos_meta}</td>
                      <td>{m.leads_meta}</td>
                      <td>{formatCurrency(parseFloat(m.cpl_meta))}</td>
                      <td>{formatCurrency(parseFloat(m.inversion_meta))}</td>
                      <td>
                        <button className="mkt-btn-icon" onClick={() => openEditMeta(m)} title="Editar">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))}
                  {metas.length === 0 && <tr><td colSpan="8" className="mkt-empty">No hay metas configuradas</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* TAB: Detalle Mes */}
      {activeTab === 'detalle' && detalleMes && (
        <div className="mkt-detalle">
          <button className="mkt-btn secondary" onClick={() => setActiveTab('dashboard')}>&larr; Volver al Dashboard</button>
          <h2 className="mkt-detalle-title">Marketing {MESES[selectedMes]} {anio}</h2>

          {/* KPI Cards */}
          <div className="mkt-kpi-grid">
            <div className="mkt-kpi-card">
              <span className="mkt-kpi-title">% Ventas Marketing</span>
              <span className="mkt-kpi-value">{formatPct(detalleMes.kpis.pct_ventas_mkt)}</span>
              <div className="mkt-kpi-detail">
                <span>Ventas Tuvi: {detalleMes.indicadores?.ventas_totales || 0}</span>
                <span>Ventas Mkt: {detalleMes.indicadores?.ventas_marketing || 0}</span>
              </div>
            </div>
            <div className="mkt-kpi-card">
              <span className="mkt-kpi-title">ROAS</span>
              <span className="mkt-kpi-value big">{detalleMes.kpis.roas}x</span>
              <div className="mkt-kpi-detail">
                <span>Costos: {formatCurrency(detalleMes.costos_marketing)}</span>
                <span>Ventas $: {formatCurrency(detalleMes.ventas_mkt_dinero)}</span>
              </div>
            </div>
            <div className="mkt-kpi-card">
              <span className="mkt-kpi-title">MQL vs SQL</span>
              <span className="mkt-kpi-value">{formatPct(detalleMes.kpis.mql_vs_sql)}</span>
              <div className="mkt-kpi-detail">
                <span>Leads: {detalleMes.indicadores?.total_leads || 0}</span>
                <span>Agendamientos: {detalleMes.indicadores?.total_agendamientos || 0}</span>
              </div>
            </div>
            <div className="mkt-kpi-card">
              <span className="mkt-kpi-title">CAC Total</span>
              <span className="mkt-kpi-value">{formatCurrency(detalleMes.kpis.cac_total)}</span>
              <div className="mkt-kpi-detail">
                <span>CAC Inversión: {formatCurrency(detalleMes.kpis.cac_inversion)}</span>
                <span>CAC %: {formatPct(detalleMes.kpis.cac_pct)}</span>
              </div>
            </div>
          </div>

          {/* Ventas del mes */}
          {detalleMes.ventas && detalleMes.ventas.length > 0 && (
            <div className="mkt-section">
              <h3>Ventas del Mes ({detalleMes.ventas.length})</h3>
              <table className="mkt-table compact">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Cliente</th>
                    <th>Proyecto</th>
                    <th>Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {detalleMes.ventas.map(v => (
                    <tr key={v.id}>
                      <td>{v.numero_venta}</td>
                      <td><strong>{v.nombre_cliente}</strong></td>
                      <td>{v.proyecto}</td>
                      <td className="highlight-blue">{formatCurrency(parseFloat(v.valor))}</td>
                    </tr>
                  ))}
                  <tr className="row-total">
                    <td colSpan="3"><strong>Total</strong></td>
                    <td className="highlight-blue"><strong>{formatCurrency(detalleMes.ventas_mkt_dinero)}</strong></td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Modal de indicadores eliminado - ahora se editan desde Registro Ventas */}

      {/* MODAL: Nueva Venta */}
      {showVentaForm && (
        <div className="mkt-modal-overlay" onClick={() => setShowVentaForm(false)}>
          <div className="mkt-modal" onClick={e => e.stopPropagation()}>
            <h3>Nueva Venta Marketing</h3>
            <form onSubmit={handleGuardarVenta}>
              <div className="mkt-form-group">
                <label>Mes</label>
                <select value={ventaForm.mes} onChange={e => setVentaForm({...ventaForm, mes: parseInt(e.target.value)})}>
                  {MESES.slice(1).map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
                </select>
              </div>
              <div className="mkt-form-group">
                <label>Nombre del Cliente</label>
                <input type="text" value={ventaForm.nombre_cliente} onChange={e => setVentaForm({...ventaForm, nombre_cliente: e.target.value})} placeholder="Nombre completo" />
              </div>
              <div className="mkt-form-group">
                <label>Proyecto</label>
                <input type="text" value={ventaForm.proyecto} onChange={e => setVentaForm({...ventaForm, proyecto: e.target.value})} placeholder="Nombre del proyecto" />
              </div>
              <div className="mkt-form-group">
                <label>Valor ($)</label>
                <input type="number" value={ventaForm.valor} onChange={e => setVentaForm({...ventaForm, valor: e.target.value})} placeholder="0" />
              </div>
              <div className="mkt-form-group">
                <label>Días de Gestión</label>
                <input type="number" value={ventaForm.dias_gestion} onChange={e => setVentaForm({...ventaForm, dias_gestion: e.target.value})} placeholder="0" min="0" />
              </div>
              <div className="mkt-form-actions">
                <button type="button" className="mkt-btn secondary" onClick={() => setShowVentaForm(false)}>Cancelar</button>
                <button type="submit" className="mkt-btn primary" disabled={guardando}>{guardando ? 'Guardando...' : 'Registrar'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL: Editar Meta */}
      {showMetaForm && (
        <div className="mkt-modal-overlay" onClick={() => setShowMetaForm(false)}>
          <div className="mkt-modal" onClick={e => e.stopPropagation()}>
            <h3>Meta — {MESES[metaForm.mes]}</h3>
            <form onSubmit={handleGuardarMeta}>
              <div className="mkt-form-group">
                <label>Mes</label>
                <select value={metaForm.mes} onChange={e => setMetaForm({...metaForm, mes: parseInt(e.target.value)})}>
                  {MESES.slice(1).map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
                </select>
              </div>
              <div className="mkt-form-row">
                <div className="mkt-form-group">
                  <label>Ventas Meta (unidades)</label>
                  <input type="number" value={metaForm.ventas_meta} onChange={e => setMetaForm({...metaForm, ventas_meta: e.target.value})} />
                </div>
                <div className="mkt-form-group">
                  <label>Ventas Meta ($)</label>
                  <input type="number" value={metaForm.meta_dinero} onChange={e => setMetaForm({...metaForm, meta_dinero: e.target.value})} placeholder="0" />
                </div>
              </div>
              <div className="mkt-form-row">
                <div className="mkt-form-group">
                  <label>Agendamientos Meta</label>
                  <input type="number" value={metaForm.agendamientos_meta} onChange={e => setMetaForm({...metaForm, agendamientos_meta: e.target.value})} />
                </div>
                <div className="mkt-form-group">
                  <label>Leads Meta</label>
                  <input type="number" value={metaForm.leads_meta} onChange={e => setMetaForm({...metaForm, leads_meta: e.target.value})} />
                </div>
              </div>
              <div className="mkt-form-row">
                <div className="mkt-form-group">
                  <label>CPL Meta ($)</label>
                  <input type="number" value={metaForm.cpl_meta} onChange={e => setMetaForm({...metaForm, cpl_meta: e.target.value})} />
                </div>
                <div className="mkt-form-group">
                  <label>Inversión Meta ($)</label>
                  <input type="number" value={metaForm.inversion_meta} onChange={e => setMetaForm({...metaForm, inversion_meta: e.target.value})} />
                </div>
              </div>
              <div className="mkt-form-actions">
                <button type="button" className="mkt-btn secondary" onClick={() => setShowMetaForm(false)}>Cancelar</button>
                <button type="submit" className="mkt-btn primary" disabled={guardando}>{guardando ? 'Guardando...' : 'Guardar'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL: Agregar Costo Fijo */}
      {showCostoForm && (
        <div className="mkt-modal-overlay" onClick={() => setShowCostoForm(false)}>
          <div className="mkt-modal small" onClick={e => e.stopPropagation()}>
            <h3>Nuevo Costo Fijo</h3>
            <form onSubmit={handleGuardarCosto}>
              <div className="mkt-form-group">
                <label>Descripción</label>
                <input type="text" value={costoForm.descripcion} onChange={e => setCostoForm({...costoForm, descripcion: e.target.value})} placeholder="Ej: Salario diseñador" />
              </div>
              <div className="mkt-form-group">
                <label>Valor Mensual ($)</label>
                <input type="number" value={costoForm.valor} onChange={e => setCostoForm({...costoForm, valor: e.target.value})} placeholder="0" />
              </div>
              <div className="mkt-form-actions">
                <button type="button" className="mkt-btn secondary" onClick={() => setShowCostoForm(false)}>Cancelar</button>
                <button type="submit" className="mkt-btn primary" disabled={guardando}>{guardando ? 'Guardando...' : 'Agregar'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL: Editar Costo Fijo */}
      {editingCosto && (
        <div className="mkt-modal-overlay" onClick={() => setEditingCosto(null)}>
          <div className="mkt-modal small" onClick={e => e.stopPropagation()}>
            <h3>Editar Costo Fijo</h3>
            <form onSubmit={handleEditarCosto}>
              <div className="mkt-form-group">
                <label>Descripción</label>
                <input type="text" value={editingCosto.descripcion} onChange={e => setEditingCosto({...editingCosto, descripcion: e.target.value})} />
              </div>
              <div className="mkt-form-group">
                <label>Valor Mensual ($)</label>
                <input type="number" value={editingCosto.valor} onChange={e => setEditingCosto({...editingCosto, valor: e.target.value})} />
              </div>
              <div className="mkt-form-actions">
                <button type="button" className="mkt-btn secondary" onClick={() => setEditingCosto(null)}>Cancelar</button>
                <button type="submit" className="mkt-btn primary" disabled={guardando}>{guardando ? 'Guardando...' : 'Guardar'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL: Editar Venta */}
      {editingVenta && (
        <div className="mkt-modal-overlay" onClick={() => setEditingVenta(null)}>
          <div className="mkt-modal" onClick={e => e.stopPropagation()}>
            <h3>Editar Venta</h3>
            <form onSubmit={handleEditarVenta}>
              <div className="mkt-form-group">
                <label>Nombre del Cliente</label>
                <input type="text" value={editingVenta.nombre_cliente} onChange={e => setEditingVenta({...editingVenta, nombre_cliente: e.target.value})} />
              </div>
              <div className="mkt-form-group">
                <label>Proyecto</label>
                <input type="text" value={editingVenta.proyecto} onChange={e => setEditingVenta({...editingVenta, proyecto: e.target.value})} />
              </div>
              <div className="mkt-form-group">
                <label>Valor ($)</label>
                <input type="number" value={editingVenta.valor} onChange={e => setEditingVenta({...editingVenta, valor: e.target.value})} />
              </div>
              <div className="mkt-form-group">
                <label>Días de Gestión</label>
                <input type="number" value={editingVenta.dias_gestion || ''} onChange={e => setEditingVenta({...editingVenta, dias_gestion: e.target.value})} min="0" />
              </div>
              <div className="mkt-form-actions">
                <button type="button" className="mkt-btn secondary" onClick={() => setEditingVenta(null)}>Cancelar</button>
                <button type="submit" className="mkt-btn primary" disabled={guardando}>{guardando ? 'Guardando...' : 'Guardar'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
